/**
 * Who said what in an outreach thread — the SPEAKER MODEL every thread viewer draws from.
 *
 * A thread can hold four parties: the owner, the owner's agent, the contact, and the contact's own
 * Symphony agent (an App thread between two Symphony users is agent-to-agent). This module decides, per
 * message, which of them wrote it, which SIDE it sits on (mine = my agent and me, on the end edge; theirs =
 * the contact and their agent, on the start edge), which bubble STYLE it wears (a person's words are a solid
 * bubble, an agent's a tinted one carrying the agent glyph), whether it opens a RUN (and so carries the
 * label), and where the DATE DIVIDERS go. It returns shapes, never strings; rendering is a thin step in each
 * component.
 *
 * ⚠️ A cross-surface CONTRACT: `native-apps/shared/outreach-thread-view/contract.json`. The same functions
 * live in iOS `Store/OutreachThreadView.swift` and Android `OutreachThreadView.kt`, and all three prove
 * themselves against that one fixture (`lib/outreach-thread-view.test.ts` here).
 *
 * WHY the legacy fallback exists (the "flat thread" bug): a message written before the outreach store
 * recorded which side wrote it (pre 2026-08-17) arrives with `author` UNKNOWN and `fromMe` false — symphony-bff
 * attributes an unrecorded side to the other party rather than put words in the caller's mouth. Read through
 * `author ?? fromMe` alone, EVERY message of such a thread fell on the contact's side as one undifferentiated
 * run: our own agent's lines looked exactly like theirs. Those messages still carry the `Message from X:` /
 * `Reply from X:` envelope (`senderName`), and the OPENING message's envelope names the thread's source — us
 * on an outbound thread, them on an inbound one — so it anchors one side.
 *
 * ⚠️ The two envelopes name the SAME party differently: the opening's is the sender the agent chose
 * ("Message from Shayke", "Message from Noa's Studio" — VMR HeldOutreachDrafts), a reply's is the replier's
 * Recipient name ("Reply from Shayke Levi" — OutreachService). So "not the anchor" never means "the other
 * side": a name is matched against EVERY party's references (whole name, or first word), and one that matches
 * nobody stays unknown.
 *
 * No runtime imports beyond the sibling pure modules, deliberately (jest loads `lib/` directly).
 */
import { strippedMessageText } from './contact-rows';
import { parseAuthor, uniqueThreads, type StageEntry, type StageThread } from './outreach-stage';

export type ThreadSpeakerKind = 'myAgent' | 'owner' | 'contact' | 'contactAgent' | 'unknown';
export type ThreadSide = 'mine' | 'theirs';
export type BubbleStyle = 'agent' | 'person';
export type ThreadDirection = 'outbound' | 'inbound';

/** A label: a catalog key with string params, verbatim text (the contact's own name), or nothing. */
export type ThreadLabel = { key: string; params: Record<string, string> } | { text: string } | null;

/** A day, for a divider or the header: today / yesterday / a date (with the year when it is not this one). */
export type DayLabel = { kind: 'today' } | { kind: 'yesterday' } | { kind: 'date'; at: string; sameYear: boolean };

/** The subset of a message the model reads. */
export interface ThreadViewMessage {
  /** The message body as stored (an envelope included) — read only by the verbatim-relay rule (`threadRows`). */
  text?: string;
  sentAt?: string;
  fromMe?: boolean;
  author?: string | null;
  senderName?: string;
  answeredByAgent?: boolean;
}

export interface ThreadViewContext {
  /** The contact's display name (`contactDisplayName`, already falling back to an address). */
  contactName: string;
  /** Which list the thread came from: outbound = we started it, inbound = they did. */
  direction: ThreadDirection;
  /** The thread's channel, raw. Empty is the in-app channel. */
  channel?: string | null;
  /**
   * The owner's own names (their Recipient name), when the surface knows them — a legacy reply envelope names
   * the replier's Recipient, so this is what puts OUR replies on our side. Omitted, an envelope that matches
   * neither the opening's sender nor the contact stays unknown.
   */
  ownerNames?: readonly string[];
}

/**
 * One transcript entry, in display order: a message (by its index in `messages`) or an owner-lane note. A
 * note carries its lane kind (`parseOwnerEventKind`, `lib/outreach-lane.ts`) and the owner's words, which is
 * what lets a TOLD note be read as private, or as the very words the agent then sent.
 */
export type ThreadEntry = { kind: 'message'; index: number } | { kind: 'note'; at?: string | null; lane?: string | null; text?: string | null };

export interface ThreadMessageView {
  speaker: ThreadSpeakerKind;
  side: ThreadSide;
  style: BubbleStyle;
  /** Draw the label above this bubble: it opens a run and has a label to draw. */
  showLabel: boolean;
  label: ThreadLabel;
  /** My agent sent the owner's note VERBATIM: the bubble is the owner's words ("You · sent by {agent}"). */
  relayed?: true;
}

/**
 * A note row: `privateNote` — a TOLD note drawn as ONE private row ("To {agent} · only you see this" over the
 * owner's words); `relayed` — a TOLD note the next bubble sent verbatim, which draws NOTHING (the bubble says it).
 * Neither: any other lane event, drawn as before.
 */
export type ThreadNoteRow = { divider: DayLabel | null; note: true; privateNote?: true; relayed?: true };

export type ThreadRow = ThreadNoteRow | ({ divider: DayLabel | null } & ThreadMessageView);

const SIDES: Record<ThreadSpeakerKind, { side: ThreadSide; style: BubbleStyle }> = {
  myAgent: { side: 'mine', style: 'agent' },
  owner: { side: 'mine', style: 'person' },
  contact: { side: 'theirs', style: 'person' },
  contactAgent: { side: 'theirs', style: 'agent' },
  unknown: { side: 'theirs', style: 'person' },
};

export function speakerSide(speaker: ThreadSpeakerKind): ThreadSide {
  return SIDES[speaker].side;
}

export function speakerStyle(speaker: ThreadSpeakerKind): BubbleStyle {
  return SIDES[speaker].style;
}

function normName(raw: string | null | undefined): string {
  return (raw ?? '').trim().replace(/\s+/g, ' ').toLowerCase();
}

function firstWord(name: string): string {
  return name.split(' ')[0] ?? '';
}

/**
 * How an envelope name matches one party's reference: 2 the whole name, 1 a first-word match, 0 not at all.
 * A first-word match needs ONE of the two to be a single word ("Shayke" ~ "Shayke Levi"): two full names with
 * different surnames ("David Levi", "David Cohen") are two different people, not a partial match.
 */
function nameMatch(name: string, ref: string): number {
  if (!ref) return 0;
  if (name === ref) return 2;
  return name === firstWord(ref) || firstWord(name) === ref ? 1 : 0;
}

/**
 * The best match against one side's references. A one-word reference that is the first word of a fuller name
 * on the SAME side is that person (an opening "Message from David" when the owner is "David Cohen"), so it is
 * read as the fuller name — otherwise "David Levi" would first-word-match the bare "David" and land on our side.
 */
function bestMatch(name: string, refs: string[]): number {
  const known = refs.filter((ref) => ref !== '');
  const full = known.filter((ref) => ref.includes(' '));
  return known
    .filter((ref) => ref.includes(' ') || !full.some((other) => firstWord(other) === ref))
    .reduce((best, ref) => Math.max(best, nameMatch(name, ref)), 0);
}

/** Whether the other side of this thread can only be a Symphony agent — the server's own `authorOf` rule:
 *  the source of any outreach is a Symphony account, and an App-channel target is one too. */
function otherSideIsAgent(ctx: ThreadViewContext): boolean {
  const channel = (ctx.channel ?? '').trim().toLowerCase();
  return ctx.direction === 'inbound' || channel === '' || channel === 'app';
}

function theirs(ctx: ThreadViewContext): ThreadSpeakerKind {
  return otherSideIsAgent(ctx) ? 'contactAgent' : 'contact';
}

/**
 * Who wrote message `index`.
 *
 * 1. The server's typed author: AGENT → my agent; OWNER_NOTE → the owner; CONTACT_AGENT → their agent;
 *    CONTACT → the contact, or their agent when it answered on its own (`answeredByAgent`).
 * 2. Otherwise (UNKNOWN / absent / unrecognised): `fromMe` → my agent.
 * 3. Otherwise the envelope, matched (case- and space-insensitive; the whole name, or a first word when one of
 *    the two names is a single word — `nameMatch`) against each side's references. Mine: the opening's sender on an outbound thread, and the owner's names. Theirs:
 *    the contact's name, and the opening's sender on an inbound thread. A match with the CONTACT is theirs
 *    before anything is put on ours; a whole-name match beats a first-word one; a first-word match on BOTH
 *    sides (contact "Ronny Cohen", owner "Ronny Elkayam", envelope "Ronny") is unknown.
 * 4. Otherwise unknown — their side, no label. Never guessed onto ours.
 */
export function threadSpeaker(messages: ThreadViewMessage[], index: number, ctx: ThreadViewContext): ThreadSpeakerKind {
  const message = messages[index];
  if (!message) return 'unknown';
  const author = parseAuthor(message.author);
  if (author === 'agent') return 'myAgent';
  if (author === 'ownerNote') return 'owner';
  if (author === 'contactAgent') return 'contactAgent';
  if (author === 'contact') return message.answeredByAgent === true ? 'contactAgent' : 'contact';
  if (message.fromMe === true) return 'myAgent';

  const name = normName(message.senderName);
  if (!name) return 'unknown';
  const anchor = normName(messages[0]?.senderName);
  const contact = normName(ctx.contactName);
  const owners = (ctx.ownerNames ?? []).map(normName);
  const outbound = ctx.direction === 'outbound';
  const mine = bestMatch(name, outbound ? [anchor, ...owners] : owners);
  const their = bestMatch(name, outbound ? [contact] : [anchor, contact]);
  if (their === 2) return theirs(ctx);
  if (mine === 2) return 'myAgent';
  if (their === 1 && mine === 1) return 'unknown';
  if (their === 1) return theirs(ctx);
  if (mine === 1) return 'myAgent';
  return 'unknown';
}

/** The label a speaker's run carries. */
export function threadSpeakerLabel(speaker: ThreadSpeakerKind, ctx: ThreadViewContext): ThreadLabel {
  const name = ctx.contactName.trim();
  switch (speaker) {
    case 'myAgent':
      return { key: 'outreach.thread.speaker.myAgent', params: {} };
    case 'owner':
      return { key: 'outreach.thread.speaker.you', params: {} };
    case 'contactAgent':
      return { key: 'outreach.thread.speaker.contactAgent', params: { name } };
    case 'contact':
      return name ? { text: name } : null;
    case 'unknown':
      return null;
  }
}

// ── Days ──────────────────────────────────────────────────────────────────────────────────────

function parseMs(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? ms : null;
}

/** `YYYY-MM-DD` of an instant in a time zone (the device's when omitted). */
function dayKey(ms: number, timeZone?: string): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date(ms));
}

function previousDayKey(key: string): string {
  const [y, m, d] = key.split('-').map(Number);
  const prev = new Date(Date.UTC(y, m - 1, d - 1));
  return prev.toISOString().slice(0, 10);
}

/** The day an instant falls on, relative to `now`, in `timeZone`. Null for a missing / unreadable instant. */
export function dayLabel(at: string | null | undefined, now: Date, timeZone?: string): DayLabel | null {
  const ms = parseMs(at);
  if (ms === null || !at) return null;
  const key = dayKey(ms, timeZone);
  const today = dayKey(now.getTime(), timeZone);
  if (key === today) return { kind: 'today' };
  if (key === previousDayKey(today)) return { kind: 'yesterday' };
  return { kind: 'date', at, sameYear: key.slice(0, 4) === today.slice(0, 4) };
}

// ── Rows ──────────────────────────────────────────────────────────────────────────────────────

/** Text as the relay rule compares it: the stored envelope stripped, whitespace collapsed, case folded. */
export function relayText(raw: string | null | undefined): string {
  return strippedMessageText(raw ?? '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

/**
 * The VERBATIM RELAYS of a transcript: which TOLD notes my agent then sent word for word. A TOLD note relays
 * when the first message after it (other notes between are skipped; any message ends the search) is my
 * agent's, not already claimed by an earlier note (the first note in entry order wins), and its text equals the note's under `relayText`. Returns
 * the entry positions of the relayed notes and the message indexes that relayed them.
 */
function verbatimRelays(
  messages: ThreadViewMessage[],
  entries: ThreadEntry[],
  speakers: ThreadSpeakerKind[],
): { notes: Set<number>; messages: Set<number> } {
  const notes = new Set<number>();
  const relayed = new Set<number>();
  entries.forEach((entry, position) => {
    if (entry.kind !== 'note' || entry.lane !== 'told') return;
    const words = relayText(entry.text);
    if (!words) return;
    const next = entries.slice(position + 1).find((later) => later.kind === 'message');
    if (!next || next.kind !== 'message') return;
    if (speakers[next.index] !== 'myAgent' || relayed.has(next.index)) return;
    if (relayText(messages[next.index]?.text) !== words) return;
    notes.add(position);
    relayed.add(next.index);
  });
  return { notes, messages: relayed };
}

/**
 * The rows a thread draws, one per entry: a day divider before the first entry of each calendar day (an
 * entry with no time draws none and does not change the day), and for a message its speaker, side, style and
 * label.
 *
 * - RUNS: a message opens a run — shows its label — when it is the first message, when its speaker differs
 *   from the previous message's, when a divider was drawn since that message, or when an owner-lane row was
 *   drawn since it (the lane interrupts the correspondence, so the next bubble says again who is speaking).
 * - RELAY: a TOLD note my agent then sent verbatim (`verbatimRelays`) draws nothing — no row, no divider, no
 *   run break — and that bubble is the OWNER's words: speaker owner (my side, a person's bubble), labelled
 *   "You · sent by {agent}", the label always shown, `relayed`. It is its own run: a bubble after it in the
 *   agent's own words, or the owner's own, labels again.
 * - PRIVATE: every other TOLD note is a `privateNote` row; other lane kinds are plain note rows.
 */
export function threadRows(
  messages: ThreadViewMessage[],
  ctx: ThreadViewContext,
  now: Date,
  entries?: ThreadEntry[],
  timeZone?: string,
): ThreadRow[] {
  const list: ThreadEntry[] = entries ?? messages.map((_, index) => ({ kind: 'message' as const, index }));
  const speakers = messages.map((_, index) => threadSpeaker(messages, index, ctx));
  const relays = verbatimRelays(messages, list, speakers);
  const rows: ThreadRow[] = [];
  let currentDay: string | null = null;
  let previousRun: string | null = null;
  let breakSincePrevious = false;
  list.forEach((entry, position) => {
    if (entry.kind === 'note' && relays.notes.has(position)) {
      rows.push({ divider: null, note: true, relayed: true });
      return;
    }
    const at = entry.kind === 'message' ? messages[entry.index]?.sentAt : entry.at;
    const ms = parseMs(at);
    let divider: DayLabel | null = null;
    if (ms !== null) {
      const key = dayKey(ms, timeZone);
      if (key !== currentDay) {
        currentDay = key;
        divider = dayLabel(at, now, timeZone);
        breakSincePrevious = true;
      }
    }
    if (entry.kind === 'note') {
      rows.push(entry.lane === 'told' ? { divider, note: true, privateNote: true } : { divider, note: true });
      breakSincePrevious = true;
      return;
    }
    if (relays.messages.has(entry.index)) {
      const label: ThreadLabel = { key: 'outreach.thread.speaker.youViaAgent', params: {} };
      rows.push({ divider, speaker: 'owner', side: speakerSide('owner'), style: speakerStyle('owner'), showLabel: true, label, relayed: true });
      previousRun = 'relayed';
      breakSincePrevious = false;
      return;
    }
    const speaker = speakers[entry.index] ?? 'unknown';
    const label = threadSpeakerLabel(speaker, ctx);
    const opensRun = previousRun === null || previousRun !== speaker || breakSincePrevious;
    rows.push({ divider, speaker, side: speakerSide(speaker), style: speakerStyle(speaker), showLabel: opensRun && label !== null, label });
    previousRun = speaker;
    breakSincePrevious = false;
  });
  return rows;
}

// ── A task's sent outreach ────────────────────────────────────────────────────────────────────

/**
 * The rows of one SENT outreach on a task's "Messages sent" list. The list reads the task summary's messages
 * (`mine` per message, no times — it draws no day dividers, so a day must not break a run there either), and
 * borrows each message's typed author and envelope from the contact book's thread when the book holds the SAME
 * messages (`book`, joined by outreach id, equal length). On a join miss — or a book thread that does not line
 * up — a message that is not ours is the contact's: it carries the contact's name as its envelope, so it reads
 * as the contact (their agent on the App channel) instead of an unlabelled unknown. The surface draws each
 * bubble on the row's `side`, so the label and the side come from one decision and cannot disagree.
 */
export function sentOutreachRows(
  sent: readonly { mine: boolean }[],
  book: readonly ThreadViewMessage[] | null | undefined,
  ctx: { contactName: string; channel?: string | null; ownerNames?: readonly string[] },
  now: Date,
): ThreadRow[] {
  const aligned = book && book.length === sent.length ? book : undefined;
  const messages: ThreadViewMessage[] = sent.map((m, i) => ({
    fromMe: m.mine,
    author: aligned?.[i]?.author,
    answeredByAgent: aligned?.[i]?.answeredByAgent,
    senderName: aligned ? aligned[i]?.senderName : m.mine ? undefined : ctx.contactName,
  }));
  return threadRows(messages, { ...ctx, direction: 'outbound' }, now);
}

// ── Header ────────────────────────────────────────────────────────────────────────────────────

/**
 * Who started the thread, and when: outbound → "{agent} started this for you"; inbound → "{name}'s agent
 * started this" (the source of any outreach is a Symphony account). The day is `createdAt`, else the opening
 * message's time; with neither, the sentence stands alone. Rendered as `sentence + " · " + day`.
 */
export function threadStartedBy(
  thread: { createdAt?: string | null; messages?: ThreadViewMessage[] | null },
  direction: ThreadDirection,
  contactName: string,
  now: Date,
  timeZone?: string,
): { label: ThreadLabel; day: DayLabel | null } {
  const label: ThreadLabel =
    direction === 'outbound'
      ? { key: 'outreach.thread.startedByMyAgent', params: {} }
      : { key: 'outreach.thread.startedByContactAgent', params: { name: contactName.trim() } };
  const at = parseMs(thread.createdAt) !== null ? thread.createdAt : thread.messages?.[0]?.sentAt;
  return { label, day: dayLabel(at, now, timeZone) };
}

// ── The contact timeline ──────────────────────────────────────────────────────────────────────

/** One of a contact's threads, as the timeline reads it. `direction` is which list it came from. */
export interface TimelineThread {
  outreachId: string;
  direction: ThreadDirection;
  channel?: string | null;
  subject?: string | null;
  createdAt?: string | null;
  messages: ThreadViewMessage[];
}

/**
 * Where a thread begins (or picks up again) in the timeline: the thread (its index in the input and its id),
 * whether it is a RETURN to a thread already shown above (`continued`), its subject (trimmed; null when it has
 * none — the surface then titles it by `threadTitle`'s creation-date fallback), and the day it started
 * (`createdAt`, else its opening message — `threadStartedBy`'s rule). Rendered "Lunch · Sep 8".
 */
export interface TimelineTopic {
  thread: number;
  outreachId: string;
  continued: boolean;
  subject: string | null;
  day: DayLabel | null;
}

export type TimelineRow =
  | { divider: DayLabel | null; topic: TimelineTopic }
  | ({ divider: DayLabel | null; thread: number; index: number } & ThreadMessageView);

/**
 * Every outreach thread with one contact, as ONE chronological transcript — the contact page's
 * "Full conversation".
 *
 * - ORDER: the threads are merged by time, each kept in its own WIRE order (a k-way merge, never a re-sort of a
 *   thread's messages). A message with no time stands at its predecessor's time in the same thread (else the
 *   thread's `createdAt`), so it never leaves its thread's place. A tie goes to the thread listed first. A
 *   thread with no messages is a topic alone, at its `createdAt`; with no time at all, a thread sorts last.
 * - TOPICS: a topic row opens every switch of thread — its first appearance and every return to it
 *   (`continued`), so each message sits under the thread it belongs to and the reader can open that thread.
 * - SPEAKERS: each message is read by `threadSpeaker` against ITS OWN thread (direction and channel differ
 *   between a contact's threads; `ownerNames` are the same for all), labelled by `threadSpeakerLabel`.
 * - DAYS: a day divider before the first row of each calendar day, across threads; when a topic opens that
 *   day, the topic row carries it (the divider sits above the topic), and the message under it carries none.
 * - RUNS: a label shows on a speaker change, after a day divider, and after a topic row — the same speaker in
 *   another thread is another conversation.
 *
 * Owner-lane notes are not part of the timeline: they belong to one thread and are read in its viewer.
 */
export function contactTimelineRows(
  threads: TimelineThread[],
  contactName: string,
  now: Date,
  timeZone?: string,
  ownerNames: readonly string[] = [],
): TimelineRow[] {
  type Item = { thread: number; index: number; time: number };
  const queues: Item[][] = threads.map((thread, t) => {
    const created = parseMs(thread.createdAt);
    if (thread.messages.length === 0) return [{ thread: t, index: -1, time: created ?? Number.POSITIVE_INFINITY }];
    let carried: number | null = created;
    return thread.messages.map((message, index) => {
      const own = parseMs(message.sentAt);
      const time = own ?? carried ?? Number.POSITIVE_INFINITY;
      if (own !== null) carried = own;
      return { thread: t, index, time };
    });
  });

  const heads = queues.map(() => 0);
  const ordered: Item[] = [];
  for (;;) {
    let pick = -1;
    for (let t = 0; t < queues.length; t++) {
      const head = queues[t][heads[t]];
      if (!head) continue;
      if (pick === -1 || head.time < queues[pick][heads[pick]].time) pick = t;
    }
    if (pick === -1) break;
    ordered.push(queues[pick][heads[pick]]);
    heads[pick]++;
  }

  const rows: TimelineRow[] = [];
  const shown = new Set<number>();
  let currentThread = -1;
  let currentDay: string | null = null;
  let previousSpeaker: ThreadSpeakerKind | null = null;
  let breakSincePrevious = false;
  for (const item of ordered) {
    const thread = threads[item.thread];
    const at = item.index >= 0 ? thread.messages[item.index]?.sentAt : thread.createdAt;
    const ms = parseMs(at);
    let divider: DayLabel | null = null;
    if (ms !== null) {
      const key = dayKey(ms, timeZone);
      if (key !== currentDay) {
        currentDay = key;
        divider = dayLabel(at, now, timeZone);
        breakSincePrevious = true;
      }
    }
    if (item.thread !== currentThread) {
      currentThread = item.thread;
      const subject = (thread.subject ?? '').trim();
      const startedAt = parseMs(thread.createdAt) !== null ? thread.createdAt : thread.messages[0]?.sentAt;
      rows.push({
        divider,
        topic: {
          thread: item.thread,
          outreachId: thread.outreachId,
          continued: shown.has(item.thread),
          subject: subject || null,
          day: dayLabel(startedAt, now, timeZone),
        },
      });
      shown.add(item.thread);
      divider = null;
      breakSincePrevious = true;
    }
    if (item.index < 0) continue;
    const ctx: ThreadViewContext = { contactName, direction: thread.direction, channel: thread.channel, ownerNames };
    const speaker = threadSpeaker(thread.messages, item.index, ctx);
    const label = threadSpeakerLabel(speaker, ctx);
    const opensRun = previousSpeaker === null || previousSpeaker !== speaker || breakSincePrevious;
    rows.push({
      divider,
      thread: item.thread,
      index: item.index,
      speaker,
      side: speakerSide(speaker),
      style: speakerStyle(speaker),
      showLabel: opensRun && label !== null,
      label,
    });
    previousSpeaker = speaker;
    breakSincePrevious = false;
  }
  return rows;
}

/**
 * A contact entry's threads as the timeline reads them — through `uniqueThreads`, so a self-outreach is read
 * once (its outbound copy), and in that order (outbound, then inbound), which is the timeline's tie-break.
 */
export function timelineThreads<
  T extends StageThread & { outreachId?: string; channel?: string; subject?: string; createdAt?: string; messages?: ThreadViewMessage[] },
>(entry: StageEntry<T>): { threads: TimelineThread[]; source: T[] } {
  const unique = uniqueThreads(entry);
  return {
    threads: unique.map(({ thread, direction }) => ({
      outreachId: thread.outreachId ?? '',
      direction,
      channel: thread.channel,
      subject: thread.subject,
      createdAt: thread.createdAt,
      messages: thread.messages ?? [],
    })),
    source: unique.map(({ thread }) => thread),
  };
}
